'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { SocialProfile } from '@/lib/social/types';
import type { SocialStory, StoryFeed, StorySettings } from '@/lib/social/story-types';
import { socialApi, useSocial, useSocialResource } from './client';
import { Avatar, ErrorState, SocialDialog } from './ui';
import { ProfileImageViewer } from './profile-image-viewer';
import { StoryAudienceEditor, StoryViewer } from './stories';
import { StoryEditor } from './story-editor';

export function ProfileStoryAvatar({ profile }: { profile: SocialProfile }) {
  const { profile: viewer } = useSocial();
  const avatar = <Avatar profile={profile} large className="social-profile-avatar rounded-full ring-4 ring-card" />;
  const fallback = profile.avatarUrl ? <ProfileImageViewer src={profile.avatarUrl} name={profile.displayName} kind="photo" className="rounded-full">{avatar}</ProfileImageViewer> : avatar;
  if (!viewer || profile.blocked || profile.private && !['self', 'accepted'].includes(profile.relationship)) return fallback;
  return <AccessibleProfileStories key={`${viewer.id}:${profile.id}:${profile.relationship}`} profile={profile} viewerId={viewer.id} avatar={avatar} fallback={fallback} />;
}

function AccessibleProfileStories({ profile, viewerId, avatar, fallback }: { profile: SocialProfile; viewerId: string; avatar: ReactNode; fallback: ReactNode }) {
  const trigger = useRef<HTMLButtonElement>(null);
  const [stories, setStories] = useState<SocialStory[]>([]), [now, setNow] = useState(() => Date.now());
  const [activeId, setActiveId] = useState<string | null>(null), [seen, setSeen] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ story: SocialStory; content: boolean } | null>(null);
  const settings = useSocialResource<{ settings: StorySettings }>(editing ? '/stories/settings' : null, viewerId);

  useEffect(() => {
    let controller: AbortController | undefined;
    async function load() {
      controller?.abort();
      const request = new AbortController(); controller = request;
      try {
        const all: SocialStory[] = [], cursors = new Set<string>();
        let cursor: string | null = null;
        // A page can be empty after permission filtering but still have older
        // readable stories. Always finish this author's pagination.
        do {
          const params = new URLSearchParams({ authorId: profile.id });
          if (cursor) { params.set('cursor', cursor); cursors.add(cursor); }
          const feed = await socialApi<StoryFeed>(`/stories?${params}`, { signal: request.signal });
          all.push(...(feed.stories ?? []));
          cursor = feed.nextCursor;
        } while (cursor && !cursors.has(cursor) && !request.signal.aborted);
        if (!request.signal.aborted) {
          setStories([...new Map(all.filter(story => story.author.id === profile.id).map(story => [story.id, story])).values()].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)));
          setActiveId(current => all.some(story => story.id === current) ? current : null);
          setNow(Date.now());
        }
      } catch { if (!request.signal.aborted) setStories([]); }
    }
    const refresh = () => { if (!document.hidden) void load(); };
    const accessChanged = () => { setStories([]); setActiveId(null); refresh(); };
    void load();
    const timer = setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('social:stories-changed', refresh);
    window.addEventListener('social:access-changed', accessChanged);
    return () => {
      controller?.abort(); clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('social:stories-changed', refresh);
      window.removeEventListener('social:access-changed', accessChanged);
    };
  }, [profile.id, viewerId]);

  const available = stories.filter(story => Date.parse(story.expiresAt) > now);
  const nextExpiry = Math.min(...available.map(story => Date.parse(story.expiresAt)));
  useEffect(() => {
    if (!Number.isFinite(nextExpiry)) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, nextExpiry - Date.now()));
    return () => clearTimeout(timer);
  }, [nextExpiry]);
  const unread = available.some(story => !story.seen && !seen.includes(story.id));
  const index = available.findIndex(story => story.id === activeId);
  const label = `View ${profile.id === viewerId ? 'your' : `${profile.displayName}’s`} stories${unread ? ', unseen' : ''}`;
  const close = () => { setActiveId(null); requestAnimationFrame(() => trigger.current?.focus()); };
  return <>
    {available.length ? <button ref={trigger} type="button" aria-label={label} title={label} aria-haspopup="dialog" data-story-ring={unread ? 'unseen' : 'seen'} className="social-profile-story-avatar block rounded-full focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-ring" onClick={() => setActiveId((available.find(story => !story.seen && !seen.includes(story.id)) ?? available[0]).id)}>{avatar}</button> : fallback}
    {activeId && <StoryViewer key={activeId} id={activeId} ownId={viewerId} onClose={close} hasPrevious={index > 0}
      onMove={direction => { const next = available[index + direction]; if (next) setActiveId(next.id); else close(); }}
      onSeen={id => setSeen(current => current.includes(id) ? current : [...current, id])}
      onEdit={story => { setActiveId(null); setEditing({ story, content: false }); }}
      onEditContent={story => { setActiveId(null); setEditing({ story, content: true }); }}
      onDeleted={() => { setStories(current => current.filter(story => story.id !== activeId)); setActiveId(null); }} />}
    {editing && !settings.data && <SocialDialog title={editing.content ? 'Edit story' : 'Change story audience'} onClose={() => setEditing(null)}>{settings.error ? <ErrorState message={settings.error} retry={settings.reload} /> : <p role="status">Loading story settings…</p>}</SocialDialog>}
    {editing && settings.data && (editing.content ? <StoryEditor initial={editing.story} settings={settings.data.settings} onClose={() => setEditing(null)} onSaved={story => { setStories(current => current.map(item => item.id === story.id ? story : item)); setEditing(null); setActiveId(story.id); }} /> : <StoryAudienceEditor story={editing.story} settings={settings.data.settings} onClose={() => setEditing(null)} onSaved={() => setEditing(null)} />)}
  </>;
}
